import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type {
  ContratoTenant,
  ContratoTenantApiDto,
  EditarContratoFormData,
  EncerrarContratoData,
  SuspenderProdutoData,
  VincularProdutoFormData,
} from '../../types/contratosTenant';

const toLocalIso = (dateOnly: string | null): string | null => {
  if (!dateOnly) return dateOnly;
  if (dateOnly.includes('T')) return dateOnly;
  return new Date(`${dateOnly}T00:00:00`).toISOString();
};

function isContratoTenantApiDto(value: unknown): value is ContratoTenantApiDto {
  if (typeof value !== 'object' || value === null) return false;
  const dto = value as Record<string, unknown>;
  return (
    typeof dto.id === 'string' &&
    typeof dto.workspaceId === 'string' &&
    typeof dto.productId === 'string' &&
    typeof dto.status === 'string' &&
    typeof dto.dueDay === 'number' &&
    typeof dto.paymentMethod === 'string' &&
    typeof dto.startAt === 'string' &&
    typeof dto.createdAt === 'string' &&
    typeof dto.updatedAt === 'string'
  );
}

function parseContratoFromApi(data: unknown): ContratoTenant {
  if (!isContratoTenantApiDto(data)) {
    throw new Error('Resposta de contrato da API em formato inesperado');
  }
  return mapContractFromApi(data);
}

export const contratosTenantService = {
  async getById(contractId: string): Promise<ContratoTenant> {
    const response = await apiGet(`/products/contracts/${contractId}`);
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to fetch contract');
    }

    return parseContratoFromApi(json.data);
  },

  async fetchByTenantId(tenantId: string): Promise<ContratoTenant[]> {
    const response = await apiGet('/products/contracts', { workspaceId: tenantId });
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to fetch contracts');
    }

    if (!Array.isArray(json.data)) {
      throw new Error('Resposta de contratos da API em formato inesperado');
    }
    return json.data.map(parseContratoFromApi);
  },

  async vincularProduto(tenantId: string, data: VincularProdutoFormData): Promise<ContratoTenant> {
    const payload = {
      workspaceId: tenantId,
      productId: data.produtoId,
      customValue: data.valorCustomizado,
      discountPercentage: data.percentualDesconto,
      dueDay: data.diaVencimento,
      paymentMethod: mapPaymentMethodToApi(data.metodoPagamento),
      expirationAlertDays: data.alertaVencimentoDias,
      variationId: data.varianteId,
      postalBilling: data.boletoCorreio
        ? data.boletoCorreio === 'SEPARADO'
          ? 'SEPARATE'
          : 'CORRELATED'
        : null,
      quantity: data.quantidade,
      trialPeriodDays: data.periodoTrial,
      autoConvertOnTrialEnd: data.autoConverterTrial,
      startAt: toLocalIso(data.dataInicio),
      endAt: toLocalIso(data.dataFim),
    };

    const response = await apiPost('/products/contracts', payload);
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to create contract');
    }

    return parseContratoFromApi(json.data);
  },

  async editarContrato(contratoId: string, data: EditarContratoFormData): Promise<ContratoTenant> {
    const payload = {
      customValue: data.valorCustomizado,
      discountPercentage: data.percentualDesconto,
      dueDay: data.diaVencimento,
      paymentMethod: mapPaymentMethodToApi(data.metodoPagamento),
      expirationAlertDays: data.alertaVencimentoDias,
      postalBilling: data.boletoCorreio
        ? data.boletoCorreio === 'SEPARADO'
          ? 'SEPARATE'
          : 'CORRELATED'
        : null,
      quantity: data.quantidade,
      trialPeriodDays: data.periodoTrial,
      autoConvertOnTrialEnd: data.autoConverterTrial,
      startAt: toLocalIso(data.dataInicio),
      endAt: toLocalIso(data.dataFim),
    };

    const response = await apiPut(`/products/contracts/${contratoId}`, payload);
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to update contract');
    }

    return parseContratoFromApi(json.data);
  },

  async suspenderContrato(dados: SuspenderProdutoData): Promise<ContratoTenant> {
    const response = await apiPatch(`/products/contracts/${dados.contratoId}/suspend`, {
      reason: dados.motivoSuspensao,
      expectedReturnDate: toLocalIso(dados.dataPrevistaRetorno),
    });
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to suspend contract');
    }

    return parseContratoFromApi(json.data);
  },

  async reativarContrato(contratoId: string): Promise<ContratoTenant> {
    const response = await apiPatch(`/products/contracts/${contratoId}/reactivate`, {});
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to reactivate contract');
    }

    return parseContratoFromApi(json.data);
  },

  async encerrarContrato(dados: EncerrarContratoData): Promise<ContratoTenant> {
    const response = await apiPatch(`/products/contracts/${dados.contratoId}/terminate`, {
      reason: dados.motivoEncerramento,
    });
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.error?.message || 'Failed to terminate contract');
    }

    return parseContratoFromApi(json.data);
  },

  async excluirContrato(contratoId: string): Promise<string> {
    const response = await apiDelete(`/products/contracts/${contratoId}`);

    if (!response.ok) {
      const json = await response.json().catch(() => null);
      throw new Error(json?.error?.message || 'Failed to delete contract');
    }

    return contratoId;
  },
};

function mapPaymentMethodToApi(method: string): string {
  const map: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'BOLETO',
    CARTAO: 'CARD',
  };
  return map[method] ?? method;
}

function mapPaymentMethodFromApi(method: string): string {
  const map: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'BOLETO',
    CARD: 'CARTAO',
  };
  return map[method] ?? method;
}

function mapStatusFromApi(status: string): string {
  const map: Record<string, string> = {
    ACTIVE: 'ATIVO',
    TRIAL: 'ATIVO',
    SUSPENDED: 'SUSPENSO',
    CANCELED: 'ENCERRADO',
    EXPIRED: 'ENCERRADO',
  };
  return map[status] ?? status;
}

function mapPostalBillingFromApi(billing: string | null): string | null {
  if (!billing) return null;
  const map: Record<string, string> = {
    SEPARATE: 'SEPARADO',
    CORRELATED: 'CORRELACIONADO',
  };
  return map[billing] ?? billing;
}

function mapContractFromApi(data: ContratoTenantApiDto): ContratoTenant {
  return {
    id: data.id,
    tenantId: data.workspaceId,
    produtoId: data.productId,
    produtoNome: data.product?.name ?? '',
    produtoCategoria: data.product?.category ?? '',
    status: mapStatusFromApi(data.status) as ContratoTenant['status'],
    valorCustomizado: data.customValue ? Number(data.customValue) : null,
    percentualDesconto: data.discountPercentage ? Number(data.discountPercentage) : null,
    valorFinal: data.finalValue ? Number(data.finalValue) : 0,
    diaVencimento: data.dueDay,
    metodoPagamento: mapPaymentMethodFromApi(
      data.paymentMethod,
    ) as ContratoTenant['metodoPagamento'],
    dataInicio: data.startAt,
    dataFim: data.endAt ?? null,
    alertaVencimentoDias: data.expirationAlertDays ?? null,
    varianteId: data.variationId ?? null,
    varianteNome: data.variation?.name ?? null,
    boletoCorreio: mapPostalBillingFromApi(
      data.postalBilling ?? null,
    ) as ContratoTenant['boletoCorreio'],
    quantidade: data.quantity ?? null,
    periodoTrial: data.trialPeriodDays ?? null,
    trialStart: data.trialStart ?? null,
    trialEnd: data.trialEnd ?? null,
    autoConverterTrial: data.autoConvertOnTrialEnd ?? false,
    motivoSuspensao: data.suspensionReason ?? null,
    dataPrevistaRetorno: data.expectedReturnDate ?? null,
    motivoEncerramento: data.terminationReason ?? null,
    faturasEmAberto: [],
    historico: [],
    versao: data.version ?? 1,
    editadoPor: data.editedByUserName ?? null,
    editadoEm: (data.version ?? 1) > 1 ? data.updatedAt : null,
    criadoEm: data.createdAt,
    atualizadoEm: data.updatedAt,
  };
}
